import { makeAutoObservable } from 'mobx'
import type { IDropdownItem, IIconProps } from 'shared/ui'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { type ISmartCallFlowRingStep } from 'entities/SmartCall/api/types'
import { IntegrationKey } from 'entities/Integrations'
import { FlowActionStore } from 'widgets/Flow'
import {
  generateSmartCallActionId,
  type ISmartCallActionStore,
  type ISmartCallFlowActionStoreProps,
} from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/Action'
import { RingFirstDropdownStore } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/Ring/ui/RingActionEditContent/RingFirstDropdown/store/RingFirstDropdownStore'
import { UsersMultiDropdownStore } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/Ring/ui/RingActionEditContent/UsersMultiDropdown/store/UsersMultiDropdownStore'
import { type ISmartCallFlowActionType } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/type'

export type IRingStoreProps = {
  id?: ISmartCallFlowActionStoreProps['id']
  parentId: ISmartCallFlowActionStoreProps['parentId']
  args?: ISmartCallFlowRingStep['args']
}

export class RingStore {
  static readonly type = 'ring' satisfies ISmartCallFlowActionType
  static title = 'Ring'
  static iconProps: IIconProps = {
    icon: 'phoneRing',
    color: 'var(--action-contained-primary-normal)',
  }
  public readonly actionStore: ISmartCallActionStore
  brunchLabel = 'If call is missed'

  constructor(private _entities: IEntitiesStore, { id, args, ...props }: IRingStoreProps) {
    this.actionStore = new FlowActionStore({
      ...props,
      id: id || generateSmartCallActionId(`${this._stepIdType.type}_${this._stepIdType.id}`),
      type: RingStore.type,
      name: RingStore.title,
      allowedNextActions: ['ring', 'playAudio'],
      edgeType: 'customEdgeWithLabel',
    })
    this.ringFirstDropdownStore = new RingFirstDropdownStore(args?.ring_type)
    this.usersMultiDropdownStore = new UsersMultiDropdownStore(args?.user_ids)
    this._duration = args?.duration ?? 30

    makeAutoObservable(this)
  }

  private _stepIdType: Pick<ISmartCallFlowRingStep, 'id' | 'type'> = {
    id: 'calling_flow',
    type: 'action',
  }

  get step(): ISmartCallFlowRingStep {
    return {
      ...this._stepIdType,
      args: {
        duration: this.duration,
        ring_type: this.ringFirstDropdownStore.value,
        user_ids: this.isUsersSelect ? this.usersMultiDropdownStore.params : [],
      },
      next: this.actionStore.nextId,
    }
  }

  setApiError = (key: string, message: string) => {}

  validate = () => {
    if (this.isUsersSelect) {
      return this.usersMultiDropdownStore.validate()
    }
    return true
  }

  get isConfigurationError() {
    if (this.isUsersSelect) {
      return !!this.usersMultiDropdownStore.error
    }
    return false
  }

  readonly ringFirstDropdownStore: RingFirstDropdownStore
  readonly usersMultiDropdownStore: UsersMultiDropdownStore

  private _duration = 30
  get duration() {
    return this._duration
  }
  setDuration = (value: number) => {
    this._duration = value
  }
  durationItems: IDropdownItem[] = Array(11)
    .fill(0)
    .map((_, i) => {
      const seconds = 10 + i * 5
      return {
        id: seconds,
        label: `${seconds} seconds`,
      }
    })

  get isUsersSelect() {
    return (
      !this.ringFirstDropdownStore.value.includes(IntegrationKey.salesmsg) &&
      !this.ringFirstDropdownStore.value.includes(IntegrationKey.hubspot)
    )
  }
}
